import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type Ref, useEffect, useRef } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    ANALYTICS_TIMEZONE,
    isInlinePanelVisible,
} from '@apps/web/features/admin_agent_notes/model/constants';
import {
    entityChips,
    type FeedStatusChip,
    feedStatusChips,
    noteHasReclassifiableWork,
} from '@apps/web/features/admin_agent_notes/model/note_row_summary';
import { buildNoteLinks } from '@apps/web/features/admin_agent_notes/model/note_trace_links';
import { NoteAddToChatButton } from '@apps/web/features/admin_agent_notes/ui/note_add_to_chat';
import {
    NoteDetailContent,
    NoteTraceActions,
} from '@apps/web/features/admin_agent_notes/ui/note_detail_body';
import {
    DOMAIN_CHIP_CLASSES,
    DOMAIN_LABELS,
    STATUS_LABELS,
} from '@apps/web/features/admin_agent_notes/ui/note_status';
import { NotesPaginationFooter } from '@apps/web/features/admin_agent_notes/ui/notes_pagination';
import {
    SourceBadge,
    SourceGlyph,
} from '@apps/web/features/admin_agent_notes/ui/source_icon';
import {
    formatDateAndTime,
    formatRelativeTime,
} from '@apps/web/lib/date_utils';
import { isEditableShortcutTarget } from '@apps/web/lib/keyboard_shortcuts';
import { cn } from '@apps/web/lib/utils';
import { type AdminAgentNoteRead } from '@libraries/typescript/api_client';

const CHIP_TONE_CLASSES: Record<FeedStatusChip['tone'], string> = {
    neutral: 'bg-muted text-muted-foreground',
    positive: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    warning: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
    critical: 'bg-red-500/10 text-red-700 dark:text-red-400',
};

function FeedCard({
    note,
    isSelected,
    onSelect,
    showSelection,
    isChecked,
    onToggleSelect,
    cardRef,
}: {
    readonly note: AdminAgentNoteRead;
    readonly isSelected: boolean;
    readonly onSelect: () => void;
    readonly showSelection: boolean;
    readonly isChecked: boolean;
    readonly onToggleSelect: (noteId: string) => void;
    readonly cardRef?: Ref<HTMLButtonElement>;
}) {
    const entities = entityChips(note);
    const isSelectable = noteHasReclassifiableWork(note);
    const card = (
        <div className="group/note-card relative flex flex-1">
            <button
                ref={cardRef}
                type="button"
                aria-pressed={isSelected}
                className={cn(
                    'flex flex-1 flex-col gap-1.5 rounded-lg border px-4 py-3 text-left',
                    'transition-colors',
                    isSelected
                        ? 'border-primary/50 bg-muted/60'
                        : 'hover:bg-muted/40',
                )}
                onClick={onSelect}
            >
                <p className="line-clamp-2 text-sm font-medium">
                    {note.content}
                </p>
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <SourceGlyph
                        source={note.source}
                        rating={note.links.source_rating}
                    />
                    <span
                        className="text-muted-foreground text-xs whitespace-nowrap"
                        title={formatDateAndTime(
                            note.created_at,
                            ANALYTICS_TIMEZONE,
                        )}
                    >
                        {formatRelativeTime(note.created_at)}
                    </span>
                    <span className="flex flex-wrap items-center gap-1">
                        {feedStatusChips(note).map((chip) => (
                            <span
                                key={chip.key}
                                className={cn(
                                    'rounded-full px-2 py-0.5 text-[11px] whitespace-nowrap',
                                    CHIP_TONE_CLASSES[chip.tone],
                                )}
                            >
                                {chip.label}
                            </span>
                        ))}
                    </span>
                </div>
                {(entities.length > 0 ||
                    note.classification_summary.domains.length > 0) && (
                    <div className="flex flex-wrap items-center gap-1">
                        {entities.map((chip) => (
                            <Badge
                                key={chip.key}
                                variant="outline"
                                className="text-[10px] font-normal"
                            >
                                {chip.name}
                                <span className="text-muted-foreground">
                                    {chip.entityType}
                                </span>
                            </Badge>
                        ))}
                        {note.classification_summary.domains.map((domain) => (
                            <span
                                key={domain}
                                className={cn(
                                    'rounded px-1.5 py-0.5 text-[10px] whitespace-nowrap',
                                    DOMAIN_CHIP_CLASSES[domain],
                                )}
                            >
                                {DOMAIN_LABELS[domain]}
                            </span>
                        ))}
                    </div>
                )}
            </button>
            <NoteAddToChatButton
                note={note}
                className="absolute top-2 right-2 z-10 opacity-0 group-hover/note-card:opacity-100 focus-within:opacity-100 focus-visible:opacity-100 data-[add-state=added]:opacity-100 data-[add-state=duplicate]:opacity-100 data-[add-state=pending]:opacity-100"
            />
        </div>
    );
    if (!showSelection) {
        return card;
    }
    return (
        <div className="flex items-start gap-2">
            <div className="flex w-5 shrink-0 justify-center pt-4">
                {isSelectable && (
                    <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => {
                            onToggleSelect(note.id);
                        }}
                        aria-label={`Select note: ${note.content.slice(0, 60)}`}
                    />
                )}
            </div>
            {card}
        </div>
    );
}

function FeedDetailPanel({
    note,
    detailError,
    isRetrying,
    hasPrevious,
    hasNext,
    onRetry,
    onNavigate,
}: {
    readonly note: AdminAgentNoteRead;
    readonly detailError: boolean;
    readonly isRetrying: boolean;
    readonly hasPrevious: boolean;
    readonly hasNext: boolean;
    readonly onRetry: (noteId: string, content?: string) => Promise<boolean>;
    readonly onNavigate: (direction: 'previous' | 'next') => void;
}) {
    const links = buildNoteLinks(note);
    return (
        <div className="bg-card flex flex-col rounded-lg border">
            <div className="flex flex-col gap-2 border-b px-5 py-4">
                <div className="flex items-start justify-between gap-2">
                    <span className="flex items-center gap-2 text-base font-semibold">
                        {STATUS_LABELS[note.status]} · {note.organization_name}
                    </span>
                    <span className="flex items-center gap-0.5">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label="Previous note"
                            disabled={!hasPrevious}
                            onClick={() => {
                                onNavigate('previous');
                            }}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label="Next note"
                            disabled={!hasNext}
                            onClick={() => {
                                onNavigate('next');
                            }}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </span>
                </div>
                <p className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
                    <SourceBadge
                        source={note.source}
                        rating={note.links.source_rating}
                    />
                    {' · '}
                    {formatDateAndTime(note.created_at, ANALYTICS_TIMEZONE)}
                    {note.created_by_user_name
                        ? ` · by ${note.created_by_user_name}`
                        : ''}
                    {note.classifier_model && (
                        <span className="font-mono">
                            {' · '}
                            {note.classifier_model}
                        </span>
                    )}
                </p>
                <NoteTraceActions
                    note={note}
                    links={links}
                    isRetrying={isRetrying}
                    onRetry={(noteId) => {
                        void onRetry(noteId);
                    }}
                />
            </div>
            <div className="px-5 py-4">
                <NoteDetailContent
                    key={note.id}
                    note={note}
                    attemptsError={detailError}
                    isRetrying={isRetrying}
                    onRetryWithContent={(noteId, content) =>
                        onRetry(noteId, content)
                    }
                />
            </div>
        </div>
    );
}

/**
 * Feed view: a card feed on the left, the selected note's full detail
 * inline on the right. The `?note=` param carries the selection so deep
 * links work.
 */
export function NotesFeed({
    notes,
    isLoading,
    selectedNote,
    detailError = false,
    isRetrying,
    hasPrevious,
    hasNext,
    page,
    totalPages,
    total,
    selectedNoteIds,
    onSelectNote,
    onToggleSelect,
    onPageChange,
    onRetry,
    onNavigate,
}: {
    readonly notes: Array<AdminAgentNoteRead>;
    readonly isLoading: boolean;
    readonly selectedNote: AdminAgentNoteRead | null;
    readonly detailError?: boolean;
    readonly isRetrying: boolean;
    readonly hasPrevious: boolean;
    readonly hasNext: boolean;
    readonly page: number;
    readonly totalPages: number;
    readonly total: number;
    readonly selectedNoteIds: ReadonlySet<string>;
    readonly onSelectNote: (noteId: string) => void;
    readonly onToggleSelect: (noteId: string) => void;
    readonly onPageChange: (page: number) => void;
    readonly onRetry: (noteId: string, content?: string) => Promise<boolean>;
    readonly onNavigate: (direction: 'previous' | 'next') => void;
}) {
    const showSelection = notes.some(noteHasReclassifiableWork);
    // Without an explicit selection, show the newest note so the panel is
    // never empty (the URL stays untouched until the user clicks a card).
    const shownNote = selectedNote ?? notes[0] ?? null;

    // Explicit selection changes reset only the two internal panes. Scrolling
    // the detail element into view would move the admin page and hide the
    // heading and filters above the feed.
    const detailPanelRef = useRef<HTMLDivElement>(null);
    const selectedCardRef = useRef<HTMLButtonElement>(null);
    const previousSelectedNoteIdRef = useRef<string | null>(null);
    useEffect(() => {
        const previousSelectedNoteId = previousSelectedNoteIdRef.current;
        const currentSelectedNoteId = selectedNote?.id ?? null;
        previousSelectedNoteIdRef.current = currentSelectedNoteId;
        if (
            currentSelectedNoteId !== null &&
            currentSelectedNoteId !== previousSelectedNoteId
        ) {
            if (detailPanelRef.current) {
                detailPanelRef.current.scrollTop = 0;
            }
            selectedCardRef.current?.scrollIntoView({ block: 'nearest' });
        }
    }, [selectedNote?.id]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (
                !isInlinePanelVisible() ||
                event.defaultPrevented ||
                event.altKey ||
                event.ctrlKey ||
                event.metaKey ||
                event.shiftKey ||
                isEditableShortcutTarget(event.target) ||
                (event.target instanceof HTMLElement &&
                    event.target.closest('[role="dialog"]') !== null)
            ) {
                return;
            }
            if (
                (event.key === 'ArrowLeft' || event.key === 'ArrowUp') &&
                hasPrevious
            ) {
                event.preventDefault();
                onNavigate('previous');
            } else if (
                (event.key === 'ArrowRight' || event.key === 'ArrowDown') &&
                hasNext
            ) {
                event.preventDefault();
                onNavigate('next');
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [hasNext, hasPrevious, onNavigate]);

    if (notes.length === 0) {
        return (
            <p className="text-muted-foreground rounded-md border px-3 py-12 text-center text-sm">
                {isLoading
                    ? 'Loading notes…'
                    : 'No notes match the current filters.'}
            </p>
        );
    }

    return (
        <div className="flex flex-col gap-3 lg:min-h-0 lg:flex-1">
            {/* Below lg the list is single-column and selecting routes to
                its own page instead of showing this inline detail pane. */}
            <div className="flex flex-col gap-4 lg:min-h-0 lg:flex-1 lg:flex-row">
                <div
                    role="region"
                    aria-label="Notes list"
                    className="flex flex-col gap-2 lg:min-h-0 lg:flex-[2] lg:overflow-y-auto lg:overscroll-contain lg:pr-1"
                >
                    {notes.map((note) => (
                        <FeedCard
                            key={note.id}
                            note={note}
                            isSelected={note.id === shownNote?.id}
                            onSelect={() => {
                                onSelectNote(note.id);
                            }}
                            showSelection={showSelection}
                            isChecked={selectedNoteIds.has(note.id)}
                            onToggleSelect={onToggleSelect}
                            cardRef={
                                note.id === shownNote?.id
                                    ? selectedCardRef
                                    : undefined
                            }
                        />
                    ))}
                </div>
                {shownNote && (
                    <div
                        ref={detailPanelRef}
                        role="region"
                        aria-label="Selected note details"
                        className="hidden lg:block lg:min-h-0 lg:flex-[3] lg:overflow-y-auto lg:overscroll-contain"
                    >
                        <FeedDetailPanel
                            note={shownNote}
                            detailError={detailError}
                            isRetrying={isRetrying}
                            hasPrevious={hasPrevious}
                            hasNext={hasNext}
                            onRetry={onRetry}
                            onNavigate={onNavigate}
                        />
                    </div>
                )}
            </div>
            <NotesPaginationFooter
                page={page}
                totalPages={totalPages}
                total={total}
                onPageChange={onPageChange}
            />
        </div>
    );
}
